import React from 'react'
import clsx from 'clsx'
import styled, { keyframes } from 'styled-components'
import ResponsiveImage from 'components/Common/ResponsiveImage'

const kenBurnsZoom = keyframes`
  0% {
    transform: scale(1) translate(0, 0);
  }
  100% {
    transform: scale(1.15) translate(-2%, -2%);
  }
`

const kenBurnsPanLeftToRight = keyframes`
  0% {
    transform: scale(1.15) translate(-5%, 0);
  }
  100% {
    transform: scale(1.15) translate(5%, 0);
  }
`

const Container = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`

const Image = styled(ResponsiveImage)`
  /* GPU acceleration for smooth animations */
  transform: translateZ(0);
  backface-visibility: hidden;

  &.ken-burns {
    animation: ${kenBurnsZoom} 10s ease-out forwards;
    animation-play-state: paused;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  &.ken-burns-left-to-right {
    animation: ${kenBurnsPanLeftToRight} 10s ease-out forwards;
    animation-play-state: paused;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  &.is-active {
    animation-play-state: running;
  }
`

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.20) 0%,
    rgba(0, 0, 0, 0.00) 20%,
    rgba(0, 0, 0, 0.00) 60%,
    #363A45 80%
  );
`

type KenBurnsVariant = 'zoom' | 'left-to-right'

interface Props {
  imageId: string
  alt?: string
  gradientOverlay?: boolean
  kenBurnsVariant?: KenBurnsVariant
  /** Whether the slide containing this background is currently active/visible */
  isActive?: boolean
  className?: string
  aspectRatio?: string
  children?: React.ReactNode
  /** Callback when the image has loaded */
  onLoad?: () => void
}

function WrappedImageBackground({
  imageId,
  alt = '',
  gradientOverlay = true,
  kenBurnsVariant,
  isActive = true,
  aspectRatio = '13:16',
  className,
  children,
  onLoad,
}: Props) {
  const imageClassName = clsx({
    'ken-burns': kenBurnsVariant === 'zoom',
    'ken-burns-left-to-right': kenBurnsVariant === 'left-to-right',
    'is-active': isActive,
  })

  return (
    <Container className={className}>
      <Image
        id={imageId}
        alt={alt}
        aspectRatio={aspectRatio}
        fit="center"
        objectFit="cover"
        className={imageClassName}
        loading="eager"
        decoding="async"
        fetchPriority="high"
        onLoad={onLoad}
      />
      {!!gradientOverlay && <GradientOverlay />}
      {children}
    </Container>
  )
}

export default React.memo(WrappedImageBackground)
